/**
 * Modal Data Utilities
 * Consolidated getter functions for all modal data
 */

import taricData from '../taric_tables_data.json';
import divisasData from '../assets/divisas.json';

// Countries (Paises)
export function getPaises() {
  return taricData.paises.map((pais) => ({
    nombre: pais.descripcion,
    codigo: pais.codigo,
  }));
}

// Provinces (Provincias)
export function getProvincias() {
  return (taricData['provincias'] || []).map((provincia) => ({
    nombre: provincia.descripcion,
    codigo: provincia.codigo,
  }));
}

// Regimes (Regimenes)
export function getRegimenes() {
  return (taricData['regimenes'] || []).map((regimen) => ({
    nombre: regimen.descripcion,
    codigo: regimen.codigo,
  }));
}

// Customs Offices (Aduanas)
export function getAduanas(dataSource = 'aduanas-espanolas') {
  // Handle array of data sources
  if (Array.isArray(dataSource)) {
    const allAduanas = [];
    dataSource.forEach((source) => {
      const aduanasFromSource = (taricData[source] || []).map((aduana) => ({
        nombre: aduana.descripcion,
        codigo: aduana.codigo,
      }));
      allAduanas.push(...aduanasFromSource);
    });
    return allAduanas;
  }
  
  // Handle single string data source
  return (taricData[dataSource] || []).map((aduana) => ({
    nombre: aduana.descripcion,
    codigo: aduana.codigo,
  }));
}

// Export Types (Tipos de Exportación)
const exportTypes = [
  { codigo: '32', nombre: 'Dua Exportación EDI' },
  { codigo: '2048', nombre: 'Dua Recapitulativo de Exportación' },
  { codigo: '2', nombre: 'Dua Exportación Papel' },
];

export function getExportacionesTipos() {
  return exportTypes;
}

// Location Types (Tipo Ubicación)
const tipoUbicacionOptions = [
  { codigo: 'A', nombre: 'Lugar designado' },
  { codigo: 'B', nombre: 'Lugar autorizado' },
  { codigo: 'C', nombre: 'Lugar aprobado' },
  { codigo: 'D', nombre: 'Otros' },
];

export function getTipoUbicacionOptions() {
  return tipoUbicacionOptions;
}

// ISO Currencies (Divisas ISO)
export function getDivisasISO() {
  return divisasData;
}

// Delivery Terms (Condiciones Entrega)
const condicionesEntregaList = [
  { codigo: 'CFR', nombre: 'Coste y flete (C & F)' },
  { codigo: 'CIF', nombre: 'Coste, seguro y flete (CAF)' },
  { codigo: 'CIP', nombre: 'Porte y seguro pagados hasta punto de destino convenido' },
  { codigo: 'CPT', nombre: 'Porte pagado hasta punto de destino convenido' },
  { codigo: 'DAP', nombre: 'Entregada en Lugar Convenido' },
  { codigo: 'DAT', nombre: 'Entregada en Terminal' },
  { codigo: 'DDP', nombre: 'Franco despachado en aduana' },
  { codigo: 'DPU', nombre: 'Entregada en Lugar Descargada' },
  { codigo: 'EXW', nombre: 'En la fábrica' },
  { codigo: 'FAS', nombre: 'Franco al costado del buque' },
  { codigo: 'FCA', nombre: 'Franco transportista' },
  { codigo: 'FOB', nombre: 'Franco a bordo' },
  { codigo: 'XXX', nombre: 'Otras condiciones' },
];

export function getCondicionesEntrega() {
  return condicionesEntregaList;
}

// Declarant Keys (Claves Declarante)
export function getClavesDeclarante() {
  return [
    { codigo: "P", nombre: "Representación Indirecta" },
    { codigo: "I", nombre: "Representación Directa" },
    { codigo: "1", nombre: "Autodespacho" },
    { codigo: "2", nombre: "Representación directa a tenor del artículo 18. 1 del Código" },
    { codigo: "3", nombre: "Representación indirecta a tenor del artículo 18. 1 del Código" },
    { codigo: "4", nombre: "(Únicamente para Canarias) Representación directa para la ATC e indirecta para la Agencia Tributaria" },
    { codigo: "5", nombre: "(Únicamente para Canarias) Representación indirecta para la ATC y directa para la Agencia Tributaria" },
  ];
}

// Declaration 1 (Declaración 1)
export function getDeclaraciones1() {
  return [
    { 
      codigo: "CO", 
      nombre: "Declaración de expedición de mercancía comunitaria a otra parte del territorio comunitario." 
    },
    { 
      codigo: "EX", 
      nombre: "Declaración de exportación de mercancías comunitarias o no comunitarias desde la Península y Baleares o desde las Islas Canarias hacia el exterior del territorio aduanero de la Comunidad. Incluye también exportación desde Ceuta y Melilla con cualquier destino. También avituallamiento y equipamiento a buques y aeronaves." 
    },
  ];
}

// Declaration 2 (Declaración 2)
export function getDeclaraciones2() {
  return [
    { 
      codigo: "A", 
      nombre: "Declaración normal (procedimiento normal, artículo 162 del CAU). Esta clave será también la utilizada en las declaraciones acogidas al procedimiento de domiciliación cuando deba presentarse en la Aduana de forma previa al levante de la mercancía." 
    },
    { 
      codigo: "B", 
      nombre: "Declaración simplificada por falta de datos (cantidad o valor provisional, artículo 166 del CAU). Debe utilizarse esta clave cuando se trate de declaraciones provisionales por no conocerse los datos definitivos o faltar algún documento." 
    },
    { 
      codigo: "C", 
      nombre: "Declaración simplificada por falta de datos (supuesto distinto del previsto con la letra B, artículo 166 del CAU)." 
    },
    { 
      codigo: "D", 
      nombre: "Predeclaración normal (tal como se contempla en el código A) de conformidad con el artículo 171 del Código." 
    },
    { 
      codigo: "E", 
      nombre: "Predeclaración simplificada (tal como se contempla en el código B) de conformidad con el artículo 171 del Código." 
    },
    { 
      codigo: "F", 
      nombre: "Predeclaración simplificada (tal como se contempla en el código C) de conformidad con el artículo 171 del Código." 
    },
    { 
      codigo: "X", 
      nombre: "Declaración complementaria de declaración simplificada tipo B." 
    },
    { 
      codigo: "Y", 
      nombre: "Declaración complementaria de declaración simplificada tipo C." 
    },
    { 
      codigo: "Z", 
      nombre: "Declaración complementaria en el contexto de un procedimiento simplificado de inscripción en los registros (artículos 166 y 182 del CAU)." 
    },
  ];
}

// UN Codes (Códigos UN)
export function getCodigosUN() {
  return (taricData['codigos-de-localidad-de-naciones-unidas'] || []).map((item) => ({
    nombre: item.descripcion,
    codigo: item.codigo,
  }));
}

// Transport Modes Soivre (Modos Transporte Soivre)
export function getModosTransporteSoivre() {
  return (taricData['modos-de-transporte-soivre'] || []).map((modo) => ({
    nombre: modo.descripcion,
    codigo: modo.codigo,
  }));
}

// Predefined Border Transport Modes (Modos Transporte Frontera Predefinidos)
export function getModosTransporteFronteraPredefinidos() {
  return [
    { codigo: "1", nombre: "Transporte Marítimo" },
    { codigo: "2", nombre: "Transporte por Ferrocarril" },
    { codigo: "3", nombre: "Transporte por Carretera" },
    { codigo: "4", nombre: "Transporte por vía Aérea" },
    { codigo: "5", nombre: "Envíos Postales" },
    { codigo: "7", nombre: "Instalaciones de transporte fijas" },
    { codigo: "8", nombre: "Transporte por vías de navegación interior" },
    { codigo: "9", nombre: "Autopropulsión" },
    { codigo: "0", nombre: "Otros" },
  ];
}

// Border Transport Means (Medios Transporte Frontera)
export function getMediosTransporteFrontera() {
  return (taricData['medio-de-transporte-frontera'] || []).map((medio) => ({
    nombre: medio.descripcion,
    codigo: medio.codigo,
  }));
}
